import { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { useLocation } from 'react-router';
// eslint-disable-next-line import/no-unresolved
import jsPDF from 'jspdf';
// eslint-disable-next-line import/no-unresolved
import autoTable from 'jspdf-autotable';
// eslint-disable-next-line import/no-unresolved
import * as XLSX from 'xlsx';

// @mui
import {
  Box,
  Card,
  Table,
  Tooltip,
  TableBody,
  Container,
  IconButton,
  TableContainer,
  TablePagination,
  FormControlLabel,
  Switch,
  Button,
  Stack,
} from '@mui/material';

// hooks
import useTabs from '../../../hooks/useTabs';
import useSettings from '../../../hooks/useSettings';
import useTable, { getComparator, emptyRows } from '../../../hooks/useTable';

// redux-slice
import { dispatch } from '../../../redux/store';
import { getClientLineTrades } from '../../../redux/slices/operator';

// components
import Page from '../../../components/Page';
import Iconify from '../../../components/Iconify';
import Scrollbar from '../../../components/Scrollbar';
import HeaderBreadcrumbs from '../../../components/HeaderBreadcrumbs';
import { TableEmptyRows, TableHeadCustom, TableNoData, TableSelectedActions } from '../../../components/table';
import TableLoader from '../../../components/table/TableLoader';

// sections
import OperatorProjectTableRow from './OperatorProjectTableRow';
import LineTradeFilterBuilder from './FilterBuilder/LineTradeFilterBuilder';

// ----------------------------------------------------------------------

const TABLE_HEAD_LINETRADES = [
  { id: 'trader.username', label: 'Trader', align: 'left' },
  { id: 'trader.firstname', label: 'FullName', align: 'left' },
  { id: 'script.tradingsymbol', label: 'ScriptName', align: 'left' },
  { id: 'script.exchange.name', label: 'Exchange', align: 'left' },
  { id: 'buy', label: 'Buy', align: 'left' },
  { id: 'sell', label: 'Sell', align: 'left' },
  { id: 'quantity', label: 'Qty', align: 'left' },
  { id: 'profit', label: 'NetP/L', align: 'left' },
  { id: 'timeDifference', label: 'Duration(Min)', align: 'left' },
  { id: 'boughtAt', label: 'BoughtAt', align: 'left' },
  { id: 'soldAt', label: 'SoldAt', align: 'left' },
  { id: 'boughtDeviceInfo', label: 'BoughtDevice', align: 'left' },
  { id: 'soldDeviceInfo', label: 'SoldDevice', align: 'left' },
];

// ----------------------------------------------------------------------

export default function ClientLineTradeList() {
  const {
    dense,
    page,
    orderBy,
    rowsPerPage,
    setRowsPerPage,
    selected,
    setSelected,
    onSelectRow,
    onSelectAllRows,
    onSort,
    onChangeDense,
  } = useTable({
    defaultRowsPerPage: 25,
  });

  const { themeStretch } = useSettings();

  const [appliedCustomFilters, setAppliedCustomFilters] = useState([]);
  const [currentCustomFilter, setCurrentCustomFilter] = useState({});
  const [order, setOrder] = useState('desc');

  const ClientsData = useSelector((state) => state?.operator);

  const location = useLocation();
  const [currentPage, setCurrentPage] = useState(1);
  const tableData = ClientsData?.clientTrades?.docs || [];
  const { currentTab: filterStatus } = useTabs('all');

  useEffect(() => {
    dispatch(getClientLineTrades(25, 1, 'createdAt', order));
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const handleCustomFiltersApply = (filters) => {
    setAppliedCustomFilters(filters);

    // In single filter mode, use the first filter if available
    let combinedFilter = {};
    if (filters.length > 0) {
      combinedFilter = filters[0].mongoFilter;
    }    // Store the current custom filter for pagination
    setCurrentCustomFilter(combinedFilter);

    try {
      dispatch(getClientLineTrades(rowsPerPage, 1, orderBy, order, combinedFilter));
    } catch (error) {
      console.log(error);
    }
  };

  const handleClearAllFilters = () => {
    setAppliedCustomFilters([]);
    setCurrentCustomFilter({});
    try {
      dispatch(getClientLineTrades(rowsPerPage, 1, orderBy, order, {}));
    } catch (error) {
      console.log(error);
    }
  };

  const handleDeleteRows = () => {
    setSelected([]);
  };

  // Format data for export
  const formatDataForExport = (data) => {
    return data.map((row) => ({
      Trader: row?.trader?.username || '',
      FullName: `${row?.trader?.firstname || ''} ${row?.trader?.lastname || ''}`.trim(),
      Script: row?.script?.tradingsymbol || '',
      Exchange: row?.script?.exchange?.name || '',
      Buy: row?.buy || 0,
      Sell: row?.sell || 0,
      Quantity: row?.quantity || 0,
      'Net P/L': row?.profit || 0,
      'Duration (Min)': row?.timeDifference || 0,
      BoughtAt: row?.boughtAt ? new Date(row.boughtAt).toLocaleString() : '',
      SoldAt: row?.soldAt ? new Date(row.soldAt).toLocaleString() : '',
      BoughtDevice: row?.boughtDeviceInfo || '',
      SoldDevice: row?.soldDeviceInfo || '',
    }));
  };

  // Download PDF
  const handleDownloadPDF = () => {
    // eslint-disable-next-line new-cap
    const doc = new jsPDF('l', 'mm', 'a4');
    const exportData = formatDataForExport(dataFiltered);

    // Add title
    doc.setFontSize(16);
    // doc.text('Filtered Trades', 14, 15);

    // Add date
    doc.setFontSize(9);
    doc.text(`Generated: ${new Date().toLocaleString()}`, 14, 22);

    // Prepare table data
    const tableData = exportData.map(row => [
      row.Trader,
      row.FullName,
      row.Script,
      row.Exchange,
      row.Buy,
      row.Sell,
      row.Quantity,
      row['Net P/L'],
      row['Duration (Min)'],
      row.BoughtAt,
      row.SoldAt,
      row.BoughtDevice,
      row.SoldDevice,
    ]);

    // Add table using autoTable function
    autoTable(doc, {
      startY: 28,
      head: [['Trader', 'Name', 'Script', 'Exch', 'Buy', 'Sell', 'Qty', 'P/L', 'Dur', 'Bought At', 'Sold At', 'Buy Dev', 'Sell Dev']],
      body: tableData,
      styles: {
        fontSize: 7,
        cellPadding: 1.5,
        lineColor: [200, 200, 200],
        lineWidth: 0.1,
      },
      headStyles: {
        fillColor: [41, 128, 185],
        textColor: 255,
        fontStyle: 'bold',
        halign: 'center',
        fontSize: 7,
      },
      alternateRowStyles: { fillColor: [245, 245, 245] },
      columnStyles: {
        0: { cellWidth: 18 },
        1: { cellWidth: 22 },
        2: { cellWidth: 20 },
        3: { cellWidth: 15 },
        4: { cellWidth: 15, halign: 'right' },
        5: { cellWidth: 15, halign: 'right' },
        6: { cellWidth: 12, halign: 'right' },
        7: { cellWidth: 15, halign: 'right' },
        8: { cellWidth: 12, halign: 'center' },
        9: { cellWidth: 28 },
        10: { cellWidth: 28 },
        11: { cellWidth: 20 },
        12: { cellWidth: 20 },
      },
      margin: { left: 5, right: 5 },
    });

    // Save PDF
    doc.save(`client-trades-${new Date().getTime()}.pdf`);
  };

  // Download Excel
  const handleDownloadExcel = () => {
    const exportData = formatDataForExport(dataFiltered);

    // Create worksheet
    const ws = XLSX.utils.json_to_sheet(exportData);

    // Set column widths
    ws['!cols'] = [
      { wch: 15 }, // Trader
      { wch: 20 }, // FullName
      { wch: 20 }, // Script
      { wch: 12 }, // Exchange
      { wch: 12 }, // Buy
      { wch: 12 }, // Sell
      { wch: 10 }, // Quantity
      { wch: 12 }, // Net P/L
      { wch: 15 }, // Duration
      { wch: 20 }, // BoughtAt
      { wch: 20 }, // SoldAt
      { wch: 18 }, // BoughtDevice
      { wch: 18 }, // SoldDevice
    ];

    // Create workbook
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Client Trades');

    // Save file
    XLSX.writeFile(wb, `client-trades-${new Date().getTime()}.xlsx`);
  };

  // const handleViewRow = async (id) => {
  //   navigate(PATH_DASHBOARD.project.view(id));
  // };

  // const handleApproveUser = (id, type, notes, filterStatus) => {
  //   try {
  //     dispatch(putProjectList(id, type, notes, filterStatus));
  //     enqueueSnackbar('Banker Approved Successfully');
  //   } catch (error) {
  //     enqueueSnackbar(error.message);
  //   }
  // };
  // const handleRejectUser = (id, type, notes, filterStatus) => {
  //   try {
  //     dispatch(putProjectList(id, type, notes, filterStatus));
  //     enqueueSnackbar('Banker Rejected Successfully');
  //   } catch (error) {
  //     enqueueSnackbar(error.message);
  //   }
  // };
  const dataFiltered = applySortFilter({
    tableData,
    comparator: getComparator('desc', orderBy),
    filterStatus,
    rowsPerPage,
    currentPage,
  });

  const denseHeight = dense ? 52 : 72;

  const isNotFound = !dataFiltered.length;

  return (
    <Page title="Trades">
      <Container maxWidth={themeStretch ? false : 'lg'}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2, gap: 1 }}>
          <HeaderBreadcrumbs
            heading="Clients Trade"
            links={[
              { name: 'Dashboard', href: `/${location.pathname.slice(1).split('/')[0]}` },
              {
                name: 'Trades',
              },
            ]}
            sx={{ mb: 0, flex: 1 }}
          />
          <Stack
            direction="row"
            spacing={1}
            sx={{
              flexShrink: 0,
            }}
          >
            <Button
              variant="contained"
              startIcon={<Iconify icon="vscode-icons:file-type-pdf2" />}
              onClick={handleDownloadPDF}
              disabled={dataFiltered.length === 0}
              size="small"
              sx={{
                bgcolor: '#d32f2f',
                '&:hover': { bgcolor: '#c62828' },
                fontSize: '0.75rem',
                px: 1.5,
                whiteSpace: 'nowrap',
              }}
            >
              PDF
            </Button>
            <Button
              variant="contained"
              startIcon={<Iconify icon="vscode-icons:file-type-excel" />}
              onClick={handleDownloadExcel}
              disabled={dataFiltered.length === 0}
              size="small"
              sx={{
                bgcolor: '#2e7d32',
                '&:hover': { bgcolor: '#1b5e20' },
                fontSize: '0.75rem',
                px: 1.5,
                whiteSpace: 'nowrap',
              }}
            >
              Excel
            </Button>
          </Stack>
        </Box>
        <LineTradeFilterBuilder
          filterType="line-trade"
          onApplyFilters={handleCustomFiltersApply}
          onClearFilters={handleClearAllFilters}
          appliedFilters={appliedCustomFilters}
        />

        <Card>
          {/* <Tabs
            allowScrollButtonsMobile
            variant="scrollable"
            scrollButtons="auto"
            value={filterStatus}
            onChange={(e, value) => {
              setCurrentTab(value);
              // setPageCurrent(1);
              // setRowsPerPage(2);
              // if (filterRole === 'all') {
              //   dispatch(getProjectList(rowsPerPage, 1, value, order));
              // } else {
              //   dispatch(getArchivedProjects(rowsPerPage, 1, value, order));
              // }
            }}
            sx={{ px: 2, bgcolor: 'background.neutral' }}
          >
            {STATUS_OPTIONS.map((tab) => (
              <Tab disableRipple key={tab} label={tab} value={tab} />
            ))}
          </Tabs>

          <Divider /> */}

          <Scrollbar>
            <TableContainer sx={{ minWidth: 800, position: 'relative' }}>
              {selected.length > 0 && (
                <TableSelectedActions
                  dense={dense}
                  numSelected={selected.length}
                  rowCount={tableData.length}
                  onSelectAllRows={(checked) =>
                    onSelectAllRows(
                      checked,
                      tableData.map((row) => row.id)
                    )
                  }
                  actions={
                    <Tooltip title="Delete">
                      <IconButton color="primary" onClick={() => handleDeleteRows(selected)}>
                        <Iconify icon={'eva:trash-2-outline'} />
                      </IconButton>
                    </Tooltip>
                  }
                />
              )}
              <Table size={dense ? 'small' : 'medium'}>
                <TableHeadCustom
                  order={order}
                  orderBy={orderBy}
                  headLabel={TABLE_HEAD_LINETRADES}
                  rowCount={tableData.length}
                  numSelected={selected.length}
                  onSort={(id) => {
                    const isAsc = orderBy === id && order === 'asc';
                    const newOrder = isAsc ? 'desc' : 'asc';
                    setOrder(newOrder);
                    onSort(id);
                    dispatch(
                      getClientLineTrades(
                        rowsPerPage,
                        1,
                        id,
                        newOrder,
                        currentCustomFilter
                      )
                    );
                  }}
                  onSelectAllRows={(checked) =>
                    onSelectAllRows(
                      checked,
                      tableData.map((row) => row.id)
                    )
                  }
                />

                <TableBody>
                  {ClientsData?.isLoading && <TableLoader />}
                  {/* {dataFiltered.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage).map((row) => ( */}
                  {dataFiltered?.length > 0 &&
                    !ClientsData?.isLoading &&
                    dataFiltered.map((row) => (
                      <OperatorProjectTableRow
                        key={row?._id}
                        row={row}
                        filterStatus={filterStatus}
                        selected={selected.includes(row?._id)}
                        onSelectRow={() => onSelectRow(row?._id)}
                        type={'LINETRADES'}
                      />
                    ))}

                  {/* {dataFiltered?.length === 0 && ClientsData?.isLoading && <TableLoader />} */}

                  <TableEmptyRows height={denseHeight} emptyRows={emptyRows(page, rowsPerPage, tableData.length)} />

                  {dataFiltered?.length === 0 && !ClientsData?.isLoading && <TableNoData isNotFound={isNotFound} />}
                </TableBody>
              </Table>
            </TableContainer>
          </Scrollbar>

          <Box sx={{ position: 'relative' }}>
            <TablePagination
              rowsPerPageOptions={[25, 50, 100]}
              component="div"
              count={ClientsData?.clientTrades?.totalDocs || 25}
              rowsPerPage={rowsPerPage}
              labelRowsPerPage={false}
              page={ClientsData?.clientTrades?.page && !Number.isNaN(ClientsData?.clientTrades?.page) ? ClientsData?.clientTrades?.page - 1 : 0}
              onPageChange={(e) => {
                if (e.target.dataset.testid === 'KeyboardArrowRightIcon') {
                  setCurrentPage(ClientsData?.clientTrades?.page + 1);
                  dispatch(
                    getClientLineTrades(
                      rowsPerPage,
                      ClientsData?.clientTrades?.page + 1,
                      orderBy,
                      order,
                      currentCustomFilter
                    )
                  );
                } else {
                  setCurrentPage(ClientsData?.page - 1);
                  dispatch(
                    getClientLineTrades(
                      rowsPerPage,
                      ClientsData?.clientTrades?.page - 1,
                      orderBy,
                      order,
                      currentCustomFilter
                    )
                  );
                }
              }}
              onRowsPerPageChange={(event) => {
                setRowsPerPage(event.target.value);
                dispatch(getClientLineTrades(event.target.value, 1, orderBy, order, currentCustomFilter));
              }}
            />

            <FormControlLabel
              control={<Switch checked={dense} onChange={onChangeDense} />}
              label="Dense"
              sx={{ px: 3, py: 1.5, top: 0, position: { md: 'absolute' } }}
            />
          </Box>
        </Card>
      </Container>
    </Page>
  );
}

// ----------------------------------------------------------------------

function applySortFilter({ tableData, comparator, filterStatus }) {
  // console.log('TABLE DATA:', tableData, comparator, filterStatus);
  return tableData;
}
